import { Button, ModalV3, useTranslation } from '@helios/frontend-core';

import { PalmettoIcon, PalmettoToaster } from '@palmetto/dsm-components';
import { useEffect, useRef, useState } from 'react';
import usePostCouponCode from '../../../../api/products/usePostCouponCode';
import { OpenTakeover } from '../../../../components/_app/TakeoverProvider/TakeoverContext';

import { CouponConfig } from '../../../../hooks/useCouponConfig';
import { TakeoverId } from '../../../../hooks/useTakeover';
import { CouponDetails } from '../usePriceAndCouponDetails';

const localePrefix = 'productDetail.productOverview.couponIssuanceModal';

interface CouponIssuanceModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  couponDetails: CouponDetails;
  couponAnalyticsData?: CouponConfig;
  trackingId: string;
  openTakeover: OpenTakeover;
}

export const CouponIssuanceModal = ({
  isOpen,
  onDismiss,
  couponDetails,
  couponAnalyticsData,
  trackingId,
  openTakeover,
}: CouponIssuanceModalProps) => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState('');
  const hasTransitionedToTakeover = useRef(false);

  const {
    postCouponCode,
    data: postCouponCodeResponse,
    error,
    isLoading: isIssuingCoupon,
    isSuccess: isSuccessIssuingCoupon,
  } = usePostCouponCode(couponAnalyticsData);

  useEffect(() => {
    if (isSuccessIssuingCoupon && couponCode && !hasTransitionedToTakeover.current) {
      hasTransitionedToTakeover.current = true;
      onDismiss();
      openTakeover(TakeoverId.REWARDS, { couponDetails, couponCode, trackingId });
    }
  }, [isSuccessIssuingCoupon, couponCode, onDismiss, openTakeover, couponDetails, trackingId]);

  useEffect(() => {
    if (error) {
      PalmettoToaster.error({ title: t(`${localePrefix}.claimError`) });
    }
  }, [error, t]);

  useEffect(() => {
    if (postCouponCodeResponse?.couponCode) {
      setCouponCode(postCouponCodeResponse.couponCode);
    }
  }, [postCouponCodeResponse?.couponCode]);

  const ModalButtons = () => {
    return (
      <div className="flex gap-12 w-full flex-col md:flex-row-reverse">
        <Button
          emphasis="primary"
          layout="fill"
          loading={isIssuingCoupon}
          disabled={!couponDetails.hasEnoughPoints}
          onClick={() => postCouponCode(couponDetails.id)}
          trackingId={`${trackingId}-confirm-issuance`}
        >
          {t(`${localePrefix}.confirmPurchase`, { points: couponDetails.pointsCost })}
        </Button>
        <Button
          emphasis="secondary"
          layout="fill"
          onClick={onDismiss}
          disabled={isIssuingCoupon}
          trackingId={`${trackingId}-cancel-issuance`}
        >
          {t(`${localePrefix}.maybeLater`)}
        </Button>
      </div>
    );
  };

  return (
    <ModalV3
      open={isOpen}
      onClose={onDismiss}
      trackingId={`${trackingId}-coupon-modal`}
      cta={<ModalButtons />}
      bottomSheetProps={{ showCloseButton: false }}
    >
      <div className="flex flex-col w-full max-w-2xl mx-auto gap-12 text-text-primary">
        <span className="self-end label-400 text-text-info flex gap-4">
          <PalmettoIcon name="palmTree" size="sm" />
          <span>{t('common.pointsDisplay', { points: couponDetails.pointsCost })}</span>
        </span>
        <h2 className="label-500 text-center">
          {couponDetails.couponIssuanceHeader ??
            t(`${localePrefix}.redeemHeaderFallback`, {
              amount: t('common.dollar.displayValue', { num: couponDetails.discountValue }),
            })}
        </h2>
        <p className="body-400 md:pb-16">
          {couponDetails.couponIssuanceDetails ?? t(`${localePrefix}.redeemDetailsFallback`)}
        </p>
      </div>
    </ModalV3>
  );
};
